/**
 * Epicurio Journal | automatic articles from ALL published Shopify blogs.
 * Uses the tokenless Shopify Storefront API (2026-10) on the store's own origin.
 * No blog handles, API tokens, apps or manual updates are needed.
 * The Liquid-rendered list remains visible if this API call is unavailable.
 */
(() => {
  'use strict';

  const ARTICLE_FIELDS = `
    id
    title
    handle
    publishedAt
    excerpt(truncateAt: 360)
    content(truncateAt: 360)
    onlineStoreUrl
    image { url(transform: { maxWidth: 1150 }) altText }
    blog { handle title onlineStoreUrl }
  `;

  const ALL_ARTICLES = `
    query EpicurioAllArticles($first: Int!, $after: String) {
      articles(first: $first, after: $after, sortKey: PUBLISHED_AT, reverse: true) {
        nodes { ${ARTICLE_FIELDS} }
        pageInfo { hasNextPage endCursor }
      }
    }
  `;
  const ONE_BLOG = `
    query EpicurioOneBlog($handle: String!, $first: Int!, $after: String) {
      blog(handle: $handle) {
        handle
        articles(first: $first, after: $after, sortKey: PUBLISHED_AT, reverse: true) {
          nodes { ${ARTICLE_FIELDS} }
          pageInfo { hasNextPage endCursor }
        }
      }
    }
  `;
  const ALL_BLOGS = `
    query EpicurioAllBlogs($first: Int!, $after: String) {
      blogs(first: $first, after: $after, sortKey: TITLE) {
        nodes { handle title onlineStoreUrl }
        pageInfo { hasNextPage endCursor }
      }
    }
  `;

  async function storefront(query, variables) {
    const response = await fetch(`${window.location.origin}/api/2026-10/graphql.json`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query, variables }),
      credentials: 'same-origin'
    });
    if (!response.ok) throw new Error(`Shopify Storefront API: HTTP ${response.status}`);
    const payload = await response.json();
    if (payload.errors?.length) throw new Error(payload.errors.map((error) => error.message).join('; '));
    if (!payload.data) throw new Error('Shopify Storefront API nu a returnat articole');
    return payload.data;
  }

  function element(tagName, className = '', value) {
    const node = document.createElement(tagName);
    if (className) node.className = className;
    if (value != null) node.textContent = value;
    return node;
  }

  function sameStorePath(value, fallback) {
    if (!value) return fallback;
    try {
      const url = new URL(value, window.location.origin);
      if (url.protocol !== 'https:' && url.protocol !== 'http:') return fallback;
      return url.href;
    } catch (_error) {
      return fallback;
    }
  }

  function truncatedText(value, words) {
    if (!value) return '';
    const normalized = value.replace(/\s+/gu, ' ').trim();
    const wordList = normalized.split(' ');
    return wordList.length > words ? `${wordList.slice(0, words).join(' ')}…` : normalized;
  }

  function renderCard(article, featured, root) {
    const category = article.blog?.title || 'Jurnal Epicurio';
    const blogHandle = article.blog?.handle || '';
    const rootUrl = (root.dataset.ebaRootUrl || '/').replace(/\/+$/u, '');
    const fallbackUrl = `${rootUrl}/blogs/${encodeURIComponent(blogHandle)}/${encodeURIComponent(article.handle || '')}`;
    const url = sameStorePath(article.onlineStoreUrl, fallbackUrl);
    const showDates = root.dataset.ebaShowDates !== 'false';
    const showExcerpts = root.dataset.ebaShowExcerpts !== 'false';
    const excerptWords = Number.parseInt(root.dataset.ebaExcerptWords, 10) || 24;
    const articleNode = element('article', `eba-card${featured ? ' eba-card--featured' : ''}`);
    articleNode.dataset.ebaBlog = blogHandle;

    const media = element('a', 'eba-card__media');
    media.href = url;
    media.setAttribute('aria-label', `Citește articolul: ${article.title}`);
    if (article.image?.url) {
      const image = element('img', 'eba-card__image');
      image.src = sameStorePath(article.image.url, '');
      image.alt = article.image.altText || article.title;
      image.loading = featured ? 'eager' : 'lazy';
      image.decoding = 'async';
      media.appendChild(image);
    } else {
      const placeholder = element('div', 'eba-card__image-placeholder');
      placeholder.setAttribute('aria-hidden', 'true');
      placeholder.appendChild(element('span', 'eba-card__monogram', 'E'));
      placeholder.appendChild(element('span', 'eba-card__placeholder-name', 'EPICURIO'));
      media.appendChild(placeholder);
    }
    if (featured) media.appendChild(element('span', 'eba-card__feature-label', 'ARTICOL RECENT'));
    const arrow = element('span', 'eba-card__media-arrow', '↗');
    arrow.setAttribute('aria-hidden', 'true');
    media.appendChild(arrow);
    articleNode.appendChild(media);

    const copy = element('div', 'eba-card__copy');
    const meta = element('div', 'eba-card__meta');
    meta.appendChild(element('span', 'eba-card__category', category));
    if (showDates && article.publishedAt) {
      const date = new Date(article.publishedAt);
      if (!Number.isNaN(date.getTime())) {
        meta.appendChild(element('span', 'eba-card__meta-dot'));
        const time = element('time', '', new Intl.DateTimeFormat('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date));
        time.dateTime = article.publishedAt.substring(0, 10);
        meta.appendChild(time);
      }
    }
    copy.appendChild(meta);
    const title = element(featured ? 'h2' : 'h3', 'eba-card__title');
    const titleLink = element('a', '', article.title);
    titleLink.href = url;
    title.appendChild(titleLink);
    copy.appendChild(title);
    if (showExcerpts) {
      const summary = truncatedText(article.excerpt || article.content || '', excerptWords);
      if (summary) copy.appendChild(element('p', 'eba-card__excerpt', summary));
    }
    const read = element('a', 'eba-card__read');
    read.href = url;
    read.appendChild(element('span', '', root.dataset.ebaReadLabel || 'Citește povestea'));
    const inlineArrow = element('span', '', '→');
    inlineArrow.setAttribute('aria-hidden', 'true');
    read.appendChild(inlineArrow);
    copy.appendChild(read);
    articleNode.appendChild(copy);
    return articleNode;
  }

  function init(root) {
    if (root.dataset.ebaAuto !== 'true' || root.dataset.ebaAutoInitialized === 'true') return;
    root.dataset.ebaAutoInitialized = 'true';

    const panel = root.querySelector('[data-eba-auto-panel]');
    const fallback = root.querySelector('[data-eba-fallback]');
    const errorNotice = root.querySelector('[data-eba-api-error]');
    const filters = root.querySelector('[data-eba-auto-filters]');
    const featured = root.querySelector('[data-eba-auto-featured]');
    const heading = root.querySelector('[data-eba-auto-heading]');
    const grid = root.querySelector('[data-eba-auto-grid]');
    const more = root.querySelector('[data-eba-auto-more]');
    const status = root.querySelector('[data-eba-auto-status]');
    const empty = root.querySelector('[data-eba-auto-empty]');
    const list = root.querySelector('[data-eba-auto-list]');
    const archives = root.querySelector('[data-eba-auto-archives]');
    const archiveLinks = root.querySelector('[data-eba-auto-archive-links]');
    if (![panel, fallback, filters, featured, heading, grid, more, status, empty, list, archives, archiveLinks].every(Boolean)) return;

    const state = {
      activeBlog: 'all',
      sequence: 0,
      loading: false,
      loaded: 0,
      after: null,
      hasNext: false,
      activated: false,
      seen: new Set()
    };
    const pageSize = Math.min(50, Math.max(6, Number.parseInt(root.dataset.ebaStep, 10) || 12));

    function setVisible() {
      if (!state.activated) {
        fallback.hidden = true;
        panel.hidden = false;
        if (errorNotice) errorNotice.hidden = true;
        state.activated = true;
      }
    }

    function resetList() {
      featured.replaceChildren();
      grid.replaceChildren();
      featured.hidden = true;
      heading.hidden = true;
      grid.hidden = true;
      list.hidden = false;
      empty.hidden = true;
      state.loaded = 0;
      state.after = null;
      state.hasNext = false;
      state.seen.clear();
      more.hidden = true;
    }

    async function loadArticles(reset = false) {
      if (state.loading && !reset) return;
      const callNumber = ++state.sequence;
      if (reset) resetList();
      state.loading = true;
      more.hidden = true;
      status.textContent = 'Se încarcă articolele…';
      try {
        const variables = { first: pageSize, after: state.after };
        const data = state.activeBlog === 'all'
          ? await storefront(ALL_ARTICLES, variables)
          : await storefront(ONE_BLOG, { ...variables, handle: state.activeBlog });
        if (callNumber !== state.sequence) return;
        const connection = state.activeBlog === 'all' ? data.articles : data.blog?.articles;
        if (!connection) throw new Error('Arhiva blogului nu a fost găsită');
        const articles = (connection.nodes || []).filter((article) => Boolean(article?.title && article?.blog));
        for (const article of articles) {
          if (state.seen.has(article.id)) continue;
          state.seen.add(article.id);
          const isFeatured = state.loaded === 0;
          if (isFeatured) {
            featured.appendChild(renderCard(article, true, root));
            featured.hidden = false;
          } else {
            grid.appendChild(renderCard(article, false, root));
            grid.hidden = false;
            heading.hidden = false;
          }
          state.loaded++;
        }
        state.hasNext = Boolean(connection.pageInfo?.hasNextPage);
        state.after = connection.pageInfo?.endCursor || null;
        setVisible();
        more.hidden = !state.hasNext;
        list.hidden = state.loaded === 0;
        empty.hidden = state.loaded > 0;
        status.textContent = state.loaded
          ? `${state.loaded} ${state.loaded === 1 ? 'articol afișat' : 'articole afișate'}${state.hasNext ? ' — încarcă mai multe pentru a continua' : ''}`
          : '';
      } catch (error) {
        if (callNumber !== state.sequence) return;
        console.warn('[Epicurio] Nu s-a putut prelua jurnalul:', error);
        if (!state.activated) {
          if (errorNotice) errorNotice.hidden = false;
          status.textContent = '';
        } else {
          status.textContent = 'Nu am putut încărca articolele. Încearcă din nou.';
          more.hidden = false;
          more.textContent = 'Încearcă din nou';
        }
      } finally {
        if (callNumber === state.sequence) {
          state.loading = false;
          if (!more.hidden) more.disabled = false;
        }
      }
    }

    function setActiveBlog(handle) {
      if (handle === state.activeBlog) return;
      state.activeBlog = handle;
      for (const button of filters.querySelectorAll('[data-eba-auto-filter]')) {
        button.setAttribute('aria-pressed', String(button.dataset.ebaAutoFilter === handle));
      }
      more.textContent = 'Mai multe articole ↓';
      loadArticles(true);
    }

    more.addEventListener('click', () => {
      if (state.loading) return;
      more.textContent = 'Mai multe articole ↓';
      more.disabled = true;
      loadArticles(false);
    });
    filters.addEventListener('click', (event) => {
      const button = event.target.closest('[data-eba-auto-filter]');
      if (button && filters.contains(button)) setActiveBlog(button.dataset.ebaAutoFilter);
    });

    async function loadAllBlogs() {
      const discovered = new Map();
      let cursor = null;
      for (let page = 0; page < 30; page++) {
        const data = await storefront(ALL_BLOGS, { first: 100, after: cursor });
        const connection = data.blogs;
        if (!connection) throw new Error('Lista blogurilor este indisponibilă');
        for (const blog of (connection.nodes || [])) {
          if (blog?.handle && blog?.title) discovered.set(blog.handle, blog);
        }
        if (!connection.pageInfo?.hasNextPage || !connection.pageInfo.endCursor) break;
        cursor = connection.pageInfo.endCursor;
      }
      filters.querySelectorAll('[data-eba-auto-filter]:not([data-eba-auto-filter="all"])').forEach((node) => node.remove());
      archiveLinks.replaceChildren();
      const rootUrl = (root.dataset.ebaRootUrl || '/').replace(/\/+$/u, '');
      for (const blog of [...discovered.values()].sort((a, b) => a.title.localeCompare(b.title, 'ro'))) {
        const button = element('button', 'eba-filters__button', blog.title);
        button.type = 'button';
        button.dataset.ebaAutoFilter = blog.handle;
        button.setAttribute('aria-pressed', String(blog.handle === state.activeBlog));
        filters.appendChild(button);
        const link = element('a', '', blog.title);
        link.href = sameStorePath(blog.onlineStoreUrl, `${rootUrl}/blogs/${encodeURIComponent(blog.handle)}`);
        const arrow = element('span', '', '↗');
        arrow.setAttribute('aria-hidden', 'true');
        link.appendChild(arrow);
        archiveLinks.appendChild(link);
      }
      archives.hidden = discovered.size === 0;
    }

    // The first articles request decides whether it is safe to replace the
    // accessible Liquid fallback. The blog directory loads independently.
    loadArticles(true);
    loadAllBlogs().catch((error) => {
      console.warn('[Epicurio] Nu am putut încărca filtrele tuturor blogurilor:', error);
    });
  }

  function boot(scope) {
    if (scope.matches?.('[data-eba-root]')) init(scope);
    for (const root of scope.querySelectorAll('[data-eba-root]')) init(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document), { once: true });
  } else {
    boot(document);
  }
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
